'use client';

import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { SurfacePanel } from '@/components/site/page-primitives';
import { cn } from '@/lib/utils';

type FaqItem = {
  question: string;
  answer: string;
};

type FaqSection = {
  title: string;
  items: FaqItem[];
};

function normalizeForSearch(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е');
}

export function HelpFaqSection({ sections }: { sections: FaqSection[] }) {
  const [query, setQuery] = useState('');
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());

  const normalized = normalizeForSearch(query.trim());

  const filtered = useMemo(() => {
    if (!normalized) return sections;
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            normalizeForSearch(item.question).includes(normalized) ||
            normalizeForSearch(item.answer).includes(normalized),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [sections, normalized]);

  const toggleItem = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isSearching = normalized.length > 0;

  return (
    <div className="space-y-6">
      <div className="relative mx-auto max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--site-ink-muted)]" />
        <Input
          type="search"
          placeholder="Поиск по вопросам…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-sm text-[var(--site-ink-muted)]">
          Ничего не найдено. Попробуйте другой запрос.
        </p>
      ) : (
        <div className="grid gap-8 lg:grid-cols-3">
          {filtered.map((section) => (
            <div key={section.title} className="space-y-4">
              <h3 className="text-lg font-semibold text-[var(--site-ink-strong)]">{section.title}</h3>
              <div className="space-y-3">
                {section.items.map((item) => {
                  const key = `${section.title}::${item.question}`;
                  const isOpen = isSearching || openKeys.has(key);

                  return (
                    <SurfacePanel key={item.question} phoneStyle="open" className="max-sm:py-3">
                      <button
                        type="button"
                        onClick={() => toggleItem(key)}
                        className="flex w-full items-start justify-between gap-2 text-left"
                      >
                        <h4 className="text-sm font-semibold text-[var(--site-ink-strong)]">
                          {item.question}
                        </h4>
                        <ChevronDown
                          className={cn(
                            'mt-0.5 h-4 w-4 shrink-0 text-[var(--site-ink-muted)] transition-transform',
                            isOpen && 'rotate-180',
                          )}
                        />
                      </button>
                      {isOpen ? (
                        <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">
                          {item.answer}
                        </p>
                      ) : null}
                    </SurfacePanel>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
